/**
 * Feeds API - Common Types
 *
 * Shared schemas and entity re-exports used across feed endpoints.
 */

import { z } from 'zod'
import {
  FeedActivitySchema,
  ReactionSchema,
  CommentSchema,
  ContentObjectSchema,
  FeedGroupSchema,
  FeedActorSchema,
  FeedAlbumSchema,
  type FeedActivity,
  type Reaction,
  type Comment,
  type ContentObject,
  type FeedGroup,
  type FeedActor,
  type FeedAlbum
} from '../../schemas/entities/feed'

// Re-export entity types
// The web feed variant below extends FeedActivitySchema, whose object is
// ContentObjectSchema, so it carries the optional album post `album` (SCH-2109)
// without redeclaring it.
export {
  FeedActivitySchema,
  ReactionSchema,
  CommentSchema,
  ContentObjectSchema,
  FeedGroupSchema,
  FeedActorSchema,
  FeedAlbumSchema
}
export type { FeedActivity, Reaction, Comment, ContentObject, FeedGroup, FeedActor, FeedAlbum }

// ============================================================================
// Shared Schemas (Stream API format)
// ============================================================================

export const StreamReactionUserDataSchema = z.object({
  name: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  sj_school_connection_id: z.string(),
  sj_platform_user_id: z.string().nullable(),
  sj_school_name: z.string()
})

export type StreamReactionUserData = z.infer<typeof StreamReactionUserDataSchema>

export const StreamReactionUserSchema = z.object({
  created_at: z.string(),
  updated_at: z.string(),
  id: z.string(),
  data: StreamReactionUserDataSchema
})

export type StreamReactionUser = z.infer<typeof StreamReactionUserSchema>

export const StreamResultSchema = z.looseObject({
  id: z.string().optional(),
  actor: z.string().optional(),
  verb: z.string().optional(),
  object: z.string().optional(),
  target: z.string().optional(),
  time: z.string().optional(),
  data: z.record(z.string(), z.any()).optional()
})

export type StreamResult = z.infer<typeof StreamResultSchema>

export const StreamRemoveResultSchema = z.looseObject({
  id: z.string().optional(),
  removed: z.boolean().optional(),
  duration: z.string().optional()
})

export type StreamRemoveResult = z.infer<typeof StreamRemoveResultSchema>

// ============================================================================
// Feed and Attachment Types (for web app compatibility)
// ============================================================================

/**
 * Attachment type in feed activities
 */
export const AttachmentSchema = z.object({
  id: z.string().optional(),
  type: z.enum(['image', 'video', 'file', 'raw', 'auto']),
  /**
   * Nullable: a Mux-backed video whose encode has not finished has no locator
   * to send. For a finished Mux video this is the HLS manifest
   * (`https://stream.mux.com/<playbackId>.m3u8`) — which a bare
   * `<video><source type="video/mp4">` cannot play outside Safari, so render
   * Mux video through the Mux player, not a native `<video>`.
   */
  url: z.string().nullable(),
  thumbnailUrl: z.string().nullable().optional(),
  name: z.string().optional(),
  mediaPlatformId: z.string().optional(),
  /**
   * Stored MIME type. The feed list read has always sent it; it was missing
   * from this schema, which is what left the web renderer unable to tell an
   * audio `file` attachment from a PDF one — both arrive as `type: 'file'`, and
   * the MIME is the only discriminator (SCH-2130, KTD2).
   */
  mimeType: z.string().nullable().optional(),
  /** File size in bytes. Also always sent, also missing from this schema. */
  fileSize: z.number().nullable().optional(),
  /**
   * Length in seconds, set only on a row that states one — in practice an AUDIO
   * attachment. `null` or absent means "unknown length", never zero: a file the
   * device could not measure uploads without one (SCH-2130, R4a).
   */
  duration: z.number().nullable().optional(),
  /**
   * Mux public playback id — the explicit "this is a Mux video" marker, same
   * field the media-library payload carries. Absent on non-video attachments
   * and on responses from a backend predating the video-playback fix.
   */
  muxPlaybackId: z.string().nullable().optional()
})

export type Attachment = z.infer<typeof AttachmentSchema>

/**
 * Extended FeedActivity with additional properties for web app
 */
export const ExtendedFeedActivitySchema = FeedActivitySchema.extend({
  // Additional reaction formats (snake_case alternatives)
  reaction_counts: z.record(z.string(), z.number()).optional(),
  own_reactions: z.record(z.string(), z.array(z.unknown())).optional(),
  latest_reactions: z.record(z.string(), z.array(z.unknown())).optional(),
  // Comments enabled flag
  commentsEnabled: z.boolean().optional()
})

export type ExtendedFeedActivity = z.infer<typeof ExtendedFeedActivitySchema>

/**
 * Feed response structure (for accessing Feed['results'][0])
 */
export const FeedSchema = z.object({
  results: z.array(ExtendedFeedActivitySchema),
  next: z.string().optional(),
  duration: z.string().optional()
})

export type Feed = z.infer<typeof FeedSchema>

// ============================================================================
// UI Helper Types (for components that work with enriched activities)
// ============================================================================

/**
 * FeedActivity with object guaranteed to be ContentObject (not string).
 * Use this type in UI components that display enriched activity data.
 */
export type EnrichedFeedActivity = Omit<FeedActivity, 'object'> & {
  object: ContentObject
}

/**
 * Feed with enriched activities
 */
export type EnrichedFeed = {
  results: EnrichedFeedActivity[]
  next?: string
  duration?: string
}

/**
 * Type guard to check if activity object is enriched (ContentObject)
 */
export function isEnrichedActivity(activity: FeedActivity): activity is EnrichedFeedActivity {
  return (
    activity.object !== null &&
    activity.object !== undefined &&
    typeof activity.object === 'object' &&
    'objectType' in activity.object
  )
}
